'use client';

import { BusyContext } from '@/components/common';
import { getProcedureHtml } from '@/domain/qualityAssurance/api';
import { type QualityAssuranceProcedure } from '@/domain/qualityAssurance/types';
import { handleException } from '@/utils/exception';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@andritzot/metris-web-ui/feedback/dialog';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

interface ProcedureDetailDialogProps {
  selectedProcedure: QualityAssuranceProcedure;
  onClose: () => void;
}

const ProcedureDetailDialog = ({ selectedProcedure, onClose }: ProcedureDetailDialogProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getProcedureHtml(selectedProcedure.idQualityAssuranceProcedure)
      .then((html) => {
        if (iframeRef.current?.contentDocument) {
          iframeRef.current.contentDocument.body.innerHTML = html;
        }
      })
      .catch((err) => {
        toast.error(handleException(err, `Error downloading procedure: `));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedProcedure]);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle title={selectedProcedure.name} onClose={onClose} />
      <DialogContent sx={{ height: 'calc(100vh - 200px)' }}>
        <BusyContext isLoading={isLoading} />

        <Box sx={{ width: '100%', height: '100%' }}>
          <iframe
            ref={iframeRef}
            title={`${selectedProcedure.name}`}
            id={`${selectedProcedure.idQualityAssuranceProcedure}`}
            name={`${selectedProcedure.name}`}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            style={{ width: '100%', height: '100%' }}
            allowFullScreen
          />
        </Box>
      </DialogContent>
      <DialogActions primaryAction={{ label: 'Close', onClick: onClose, startIcon: <CloseOutlined /> }} />
    </Dialog>
  );
};

export default ProcedureDetailDialog;
